/**
 * `createCachedLoader` com tier persistente na Cache API da Cloudflare.
 *
 * Mecânica PORTADA de granadobr-tanstack `src/lib/fetch-cache.ts` (o "tiered
 * SWR fetch cache" que aquele site usa nas chamadas de Magento, ele próprio
 * adaptado do `fetchCache` do app VTEX) — mesma estratégia de tiers, mesmo
 * esquema de chave ancorada na zona, mesmos headers. Aqui a API exposta é a
 * de LOADER (drop-in do `createCachedLoader` do framework), porque é o que os
 * commerce loaders do miess embrulham.
 *
 * Por que não usar só o `createCachedLoader`: ele guarda num Map em memória
 * POR ISOLATE, teto de 32MB compartilhado entre todos os loaders e eviction
 * oldest-first (cachedLoader.ts:102-173). Payload de PLP tem 0,5–2MB, então
 * cabem ~16-60 entradas — e como essa camada só recebe tráfego que o edge já
 * não serviu (edge MISS), as entradas morrem antes de reuso. Resultado
 * medido: `miess/legacyProductListingPage` a 0% de hit em 7,7k chamadas;
 * `miess/getBrand` a 44,9% (quase só dedup in-flight — os 17 STALE-HITs no
 * mesmo painel provam que grava mas não sobrevive).
 *
 * Tiers (rápido → durável → origem):
 *   1. Map em memória POR WRAPPER, LRU de poucas entradas — hot reads no
 *      mesmo isolate e sem o defeito diagnosticado do Map do framework: aqui
 *      um PLP write nunca despeja entrada de outro loader.
 *   2. Cache API (`caches.default`, por colo) — sobrevive à reciclagem de
 *      isolate e é COMPARTILHADA entre isolates do mesmo colo. Chaveada só
 *      pelas props canônicas do loader, NÃO por geo/segmento — quando o edge
 *      de página (fragmentado por __seg/geo) MISSa, a ida à VTEX ainda é
 *      servida daqui. É `default`, e não `caches.open(...)`, de propósito —
 *      ver o comentário em CACHE_NAME.
 *   3. Origem (VTEX/WordPress).
 *
 * Chave da Cache API: a Cloudflare só cacheia sob o hostname da PRÓPRIA zona
 * do worker, então a chave é ancorada na origin da request corrente
 * (`RequestContext.request`) — host sintético inventado viraria no-op
 * silencioso. Fora de escopo de request (testes), a tier 2 é pulada e vale só
 * a memória. Build hash prefixa a chave lógica: redeploy orfã as entradas dos
 * DOIS tiers em lockstep com o edge (`?__v=`).
 *
 * Semântica preservada do framework:
 *  - join de chamada in-flight conta como HIT;
 *  - loaderFn que LANÇA vira MISS + `deco.loader.errors` e NUNCA é armazenado;
 *    resultado VAZIO, por outro lado, é armazenado com TTL curto (ver
 *    `negative` em CachedLoaderCfOptions);
 *  - refresh de entrada stale é floating promise com guarda (o do framework
 *    também é — não há `ctx.waitUntil` acessível daqui);
 *  - exatamente UMA emissão de métrica por chamada, mesmos labels
 *    (`layer="cachedLoader"`, `profile=<nome do loader>`) — o painel
 *    existente de hit-rate mede a melhora sem mudar nada.
 *
 * Frescor: `loaderCacheOptions(profile).maxAge` (listing 60s, static 300s)
 * decide HIT vs STALE-HIT. Janela stale (staleMs): até quando servir stale
 * enquanto o refresh corre atrás — 24h para listing/static é a MESMA
 * tolerância que o edge já aceita para o HTML dessas páginas
 * (cache-config.ts: listing swr 86400); 6h para search (cauda longa).
 * Stale-if-error fica subsumido: refresh que falha mantém a entrada elegível
 * até o fim da janela.
 *
 * Purga: redeploy (build hash na chave). ATENÇÃO: `POST /_cache/purge-loaders`
 * limpa só o Map do framework; NÃO alcança estas entradas.
 *
 * Fallback: em dev (mesma leitura de `NODE_ENV`/`DECO_CACHE_DISABLE` do
 * framework) delega ao `createCachedLoader` — métricas de BYPASS de dev
 * idênticas ao comportamento atual. Sem `caches`/escopo de request, opera
 * memória-only (paridade com o granado).
 */
import {
  recordCacheMetric,
  recordLoaderError,
  recordLoaderMetric,
  withTracing,
} from "@decocms/blocks/middleware/observability";
import { type CacheProfileName, loaderCacheOptions } from "@decocms/blocks/sdk/cacheHeaders";
import { createCachedLoader } from "@decocms/blocks/sdk/cachedLoader";
import { RequestContext } from "@decocms/blocks/sdk/requestContext";
import { keepAlive, type KeepAlivePath } from "~/sdk/requestWaitUntil";

// Mesmo padrão de src/worker-entry.ts:327 — inerte onde o define não é
// aplicado (bun test), onde a tier persistente já não roda de qualquer forma.
declare const __DECO_BUILD_HASH__: string | undefined;
const BUILD =
  typeof __DECO_BUILD_HASH__ !== "undefined" && __DECO_BUILD_HASH__ ? __DECO_BUILD_HASH__ : "dev";

// Namespace no PATH da chave, não em `caches.open(...)`: o host de produção
// do candidato é workers.dev, onde o `caches.default` comprovadamente funciona
// (a camada edge do framework dá X-Cache: HIT nele) mas o bucket NOMEADO não
// serve nada — Cache API nomeada é escopada à zona, e a exceção que arma o
// workers.dev cobre só o default. Medido ao vivo em 27/08/2026: mesmo par de
// requests com chave de loader idêntica nunca acertava o bucket nomeado,
// enquanto o default acertava na mesma janela. `caches.default` também segue
// válido quando o site cortar para a zona própria.
const CACHE_NAME = "loader-api";

// Janela stale por profile (ms) — soma-se ao fresh do profile para formar o
// `staleUntil`/`max-age` da entrada.
const STALE_MS: Partial<Record<CacheProfileName, number>> = {
  listing: 86_400_000,
  static: 86_400_000,
  search: 21_600_000,
};
const DEFAULT_STALE_MS = 21_600_000;

// L1 é por wrapper e limitado por BYTES, não só por entradas: payload real de
// PLP medido em produção = 9,7MB por entrada (/lingerie, count=36, via
// /deco/invoke em 27/08/2026). Um teto só de contagem (16) deixaria o wrapper
// da PLP reter ~155MB → exceededMemory (limite de 128MB do isolate) → o
// isolate morre e leva TODOS os caches em memória junto — o L1 viraria a
// causa do churn que ele existe para compensar.
const L1_MAX_ENTRIES = 16;
const L1_MAX_BYTES = 8 * 1024 * 1024;
// Entrada maior que isto nunca entra no L1 — fica só na Cache API (tier 2),
// onde match+parse custa dezenas de ms, contra o risco de reter MBs no heap.
//
// 3MB, e não os 2MB originais: o teto foi escolhido contra o payload de 9,7MB
// que a dieta na origem (leanVariants + maxImages) eliminou. Medido no blob do
// preview depois dela, a entrada de PLP é 1,85MB — só 143KB sob os 2MB, ou
// seja, uma página um pouco maior era rejeitada do L1 em SILÊNCIO. Com 3MB a
// folga vira ~1,1MB.
//
// Não sobe mais que isso porque o `L1_MAX_BYTES` de 8MB é o limite real de
// retenção do wrapper (~4 entradas de PLP): passar de 3MB só permitiria uma
// entrada gigante monopolizar o L1, sem aumentar quantas cabem.
const L1_MAX_ENTRY_BYTES = 3 * 1024 * 1024;

interface CacheEntry<T> {
  value: T;
  /** `JSON.length` do valor — aproximação do custo de retenção no heap. */
  bytes: number;
  /** Epoch ms após o qual a entrada está stale (serve + refresh). */
  freshUntil: number;
  /** Epoch ms após o qual a entrada não pode mais ser servida. */
  staleUntil: number;
}

// `caches.default` é exclusivo do workerd (browser tem `caches.open`, nunca
// `.default`) — o probe também é o guard de runtime. Browser nunca executa
// este caminho de qualquer forma: loaders são server-only.
const openCache = (): Cache | null =>
  (globalThis as { caches?: { default?: Cache } }).caches?.default ?? null;

const sha256Hex = async (input: string): Promise<string> => {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Request-chave da Cache API, ancorada na origin da request corrente (a CF só
 * cacheia sob o host da própria zona). `null` fora de escopo de request →
 * tier persistente pulada.
 */
const cacheRequest = async (vkey: string): Promise<Request | null> => {
  let origin: string;
  try {
    origin = new URL(RequestContext.request.url).origin;
  } catch {
    return null;
  }
  return new Request(`${origin}/__loader_cache/${CACHE_NAME}/${await sha256Hex(vkey)}`);
};

/**
 * Telemetria da tier persistente — layer própria (`loaderL2`) para NÃO poluir
 * o painel de hit-rate (que agrupa por layer `cachedLoader`/`edge`). Statuses
 * fora do enum do framework de propósito: `PUT-OK|PUT-FAIL|PUT-NOCTX|
 * PUT-NOSTORE|GET-NOCTX`, com `provider` = rota do keepAlive
 * (`als|request|floating`). É o que faltou no #173: sem isto, "o put rodou
 * floating", "o put falhou" e "o put nem rodou" são indistinguíveis de fora.
 */
const recordL2 = recordCacheMetric as (
  hit: boolean,
  profile?: string,
  decision?: string,
  layer?: string,
  provider?: string,
) => void;

const getPersistent = async <T>(
  vkey: string,
  name: string,
): Promise<{ value: T; ageMs: number; bytes: number } | null> => {
  const cache = openCache();
  if (!cache) return null;
  try {
    const req = await cacheRequest(vkey);
    if (!req) {
      // Escopo de request perdido = L2 INTEIRO desligado para esta chamada
      // (get e put). Se isto aparecer no painel, os MISS não são de storage.
      recordL2(false, name, "GET-NOCTX", "loaderL2");
      return null;
    }
    const res = await cache.match(req);
    if (!res) return null;
    const storedAt = Number(res.headers.get("x-deco-stored-at") || "0");
    const ageMs = storedAt > 0 ? Date.now() - storedAt : Infinity;
    if (!Number.isFinite(ageMs)) return null;
    // text→parse (e não res.json()) para medir os bytes sem re-stringificar —
    // o tamanho decide se a entrada pode hidratar o L1.
    const text = await res.text();
    const value = JSON.parse(text) as T;
    return { value, ageMs, bytes: text.length };
  } catch {
    // match indisponível ou body corrompido → MISS.
    return null;
  }
};

const putPersistent = async (
  vkey: string,
  json: string,
  totalMs: number,
  name: string,
): Promise<"OK" | "FAIL" | "NOCTX" | "NOSTORE"> => {
  const cache = openCache();
  if (!cache) return "NOSTORE";
  try {
    const req = await cacheRequest(vkey);
    if (!req) return "NOCTX";
    await cache.put(
      req,
      new Response(json, {
        headers: {
          "content-type": "application/json",
          "cache-control": `public, max-age=${Math.max(1, Math.ceil(totalMs / 1000))}`,
          "x-deco-stored-at": String(Date.now()),
        },
      }),
    );
    return "OK";
  } catch (err) {
    // 413/quota/indisponível — a request nunca paga pela falha do put, mas a
    // falha tem que ser VISÍVEL (wrangler tail): era muda desde o #164.
    console.error(`[cachedLoaderCf] put falhou name=${name} bytes=${json.length}`, err);
    return "FAIL";
  }
};

export interface CachedLoaderCfOptions<TResult> {
  /**
   * Resultado NEGATIVO (ex.: PLP vazia) — cacheado com TTL próprio e curto, sem
   * janela stale.
   *
   * Por que existe: até aqui um resultado vazio era mantido FORA do cache
   * (o loader lançava um sentinel), na intenção de nunca congelar uma galeria
   * vazia. O efeito real, medido em produção em 31/08/2026, é que a origem
   * passa a ser consultada em TODA request para um resultado que já sabemos
   * vazio — `/cupons` fez 188 idas à VTEX em 41 minutos, ~542ms cada, para
   * produzir um JSON-LD sem produtos. Quem impede a página vazia de congelar
   * no EDGE é o `markSectionDegraded` (ver `withEmptyPlpGuard` em
   * src/setup/commerce-loaders.ts), não a recusa desta camada em gravar.
   *
   * O TTL curto e SEM janela stale é o que separa isto do cacheamento normal:
   * a entrada negativa expira e vira MISS (vai à origem de novo), em vez de ser
   * servida stale por 24h como uma PLP legítima. Uma categoria momentaneamente
   * vazia fica vazia por no máximo `ttlMs`.
   */
  negative?: { is: (value: TResult) => boolean; ttlMs: number };

  /**
   * Eixo EXTRA da chave, lido da request corrente — para loaders cujo resultado
   * depende de algo que não está nas props.
   *
   * Caso real que obriga isto: `vtexFetch` encaminha o cookie `vtex_segment`
   * nas chamadas à VTEX (`apps-vtex/src/client.ts:322`), e o Catalog legacy
   * usa esse cookie para resolver sellers regionais — "products only available
   * via a region's seller appear as OutOfStock without it". Logo o resultado do
   * loader legacy VARIA por região com props idênticas. Sem este eixo, uma
   * entrada gravada para a região A seria servida para a região B (estoque
   * errado). Ver `vtexSegmentVary` em src/setup/commerce-loaders.ts.
   *
   * Devolva `undefined` quando não há discriminador (visitante anônimo sem CEP,
   * que é o caso comum e o dos bots) — assim esses compartilham uma única
   * entrada e o hit rate não desaba.
   */
  vary?: () => string | undefined;
}

export function createCachedLoaderCf<TProps, TResult>(
  name: string,
  loaderFn: (props: TProps) => Promise<TResult>,
  profile: CacheProfileName,
  options?: CachedLoaderCfOptions<TResult>,
): (props: TProps) => Promise<TResult> {
  const { maxAge: freshMs } = loaderCacheOptions(profile);
  const staleMs = STALE_MS[profile] ?? DEFAULT_STALE_MS;

  /**
   * Janela de vida do valor. Um resultado negativo usa o TTL curto nos DOIS
   * limites (`fresh === total`), então nunca entra em stale: expirou é MISS.
   *
   * Ponto único de verdade de propósito — leitura e escrita usam o mesmo
   * cálculo. Se o `max-age` do put e a checagem de idade do get divergissem, a
   * entrada negativa viveria no L2 além da janela e o `ttlMs` viraria decorativo.
   */
  const ttlOf = (value: TResult): { fresh: number; total: number } => {
    if (options?.negative?.is(value)) {
      const { ttlMs } = options.negative;
      return { fresh: ttlMs, total: ttlMs };
    }
    return { fresh: freshMs, total: freshMs + staleMs };
  };

  // Mesma leitura em wrap-time do framework (cachedLoader.ts:213-214).
  const env = typeof globalThis.process !== "undefined" ? globalThis.process.env : undefined;
  const isDev = env?.DECO_CACHE_DISABLE === "true" || env?.NODE_ENV === "development";

  let fallback: ((props: TProps) => Promise<TResult>) | undefined;
  const useFallback = () => (fallback ??= createCachedLoader(name, loaderFn, profile));

  // Tier 1 — LRU via ordem de inserção do Map (delete+set no toque), com
  // contabilidade de bytes.
  const store = new Map<string, CacheEntry<TResult>>();
  let l1Bytes = 0;
  const inflight = new Map<string, Promise<TResult>>();
  const refreshing = new Set<string>();

  const removeL1 = (vkey: string): void => {
    const prev = store.get(vkey);
    if (!prev) return;
    l1Bytes -= prev.bytes;
    store.delete(vkey);
  };

  const touch = (vkey: string, entry: CacheEntry<TResult>): void => {
    removeL1(vkey);
    store.set(vkey, entry);
    l1Bytes += entry.bytes;
    while (store.size > L1_MAX_ENTRIES || l1Bytes > L1_MAX_BYTES) {
      const oldest = store.keys().next().value as string | undefined;
      if (oldest === undefined) break;
      removeL1(oldest);
    }
  };

  /**
   * Grava nos dois tiers, preservando a idade quando hidrata do L2.
   * `knownBytes` vem da hidratação (texto já lido) — evita re-stringificar.
   * Entrada acima de L1_MAX_ENTRY_BYTES não hidrata o L1 (e derruba qualquer
   * versão antiga que estivesse lá, para o L1 nunca sombrear o tier 2).
   */
  const setEntry = (vkey: string, value: TResult, ageMs = 0, knownBytes?: number): void => {
    const { fresh, total } = ttlOf(value);
    let bytes = knownBytes;
    if (bytes === undefined) {
      const json = JSON.stringify(value);
      if (json === undefined) return; // não serializável: nada a guardar
      bytes = json.length;
      // `keepAlive` (ctx.waitUntil) e NÃO floating promise: o put da PLP é de
      // ~9,6MB e não terminava antes da resposta — era a causa do hit rate de
      // 2,3% neste loader contra 98,1% nos de payload pequeno. Ver
      // ~/sdk/requestWaitUntil. O `via` é `let` de propósito: o work() começa
      // síncrono dentro do keepAlive, mas a linha da métrica só roda depois do
      // primeiro await — quando o keepAlive já retornou e atribuiu o caminho.
      let via: KeepAlivePath = "floating";
      via = keepAlive(async () => {
        const outcome = await putPersistent(vkey, json, total, name);
        recordL2(outcome === "OK", name, `PUT-${outcome}`, "loaderL2", via);
      });
    }
    if (bytes > L1_MAX_ENTRY_BYTES) {
      removeL1(vkey);
      return;
    }
    const t = Date.now();
    touch(vkey, {
      value,
      bytes,
      freshUntil: t + Math.max(0, fresh - ageMs),
      staleUntil: t + Math.max(0, total - ageMs),
    });
  };

  const origin = (props: TProps) =>
    withTracing("deco.cachedLoader", () => loaderFn(props), {
      "deco.loader": name,
      "deco.cache.policy": "stale-while-revalidate",
    });

  // Revalidação em background (origem → dois tiers), com guarda para leituras
  // stale concorrentes não estourarem a origem. Falha na origem mantém o stale;
  // a próxima leitura stale tenta de novo. Entrada negativa nunca chega aqui:
  // ela não tem janela stale (ver `ttlOf`).
  const bgRefresh = (vkey: string, props: TProps): void => {
    if (refreshing.has(vkey)) return;
    refreshing.add(vkey);
    // Também via keepAlive: o refresh vai à origem (segundos) e depois grava —
    // sem waitUntil ele morria junto com a resposta e a entrada ficava presa em
    // stale até o fim da janela, servindo STALE-HIT para sempre em vez de
    // reconvergir para HIT.
    keepAlive(() =>
      origin(props)
        .then((value) => setEntry(vkey, value))
        .finally(() => refreshing.delete(vkey)),
    );
  };

  // O `provider` vai NO LUGAR de mexer no `profile`: o painel de hit-rate
  // agrupa por `profile` (= nome do loader) e continua idêntico, enquanto
  // `provider` — dimensão que a camada `cachedLoader` nunca usou, só o `swr`
  // (observability.ts:553-559) — passa a dizer QUAL tier serviu. Sem isso o
  // experimento é ambíguo: entrada enxuta cabe nos DOIS tiers ao mesmo tempo
  // (L1 volta a aceitar por ficar sob o L1_MAX_ENTRY_BYTES E o put da
  // Cache API fica menor), e um hit rate alto não diria qual das duas
  // hipóteses do #174 caiu.
  type Tier = "l1" | "l2" | "inflight" | "origin";

  const served = (decision: "HIT" | "STALE-HIT", t0: number, tier: Tier) => {
    recordCacheMetric(true, name, decision, "cachedLoader", tier);
    recordLoaderMetric(name, performance.now() - t0, decision);
  };

  return async (props: TProps): Promise<TResult> => {
    if (isDev) return useFallback()(props);

    // `vary` falho não pode derrubar a request — sem discriminador, cai no
    // pool compartilhado (que é o comportamento anterior).
    let varyKey: string | undefined;
    if (options?.vary) {
      try {
        varyKey = options.vary();
      } catch {
        varyKey = undefined;
      }
    }
    const vkey = `${BUILD}::${name}::${varyKey ? `${varyKey}::` : ""}${
      JSON.stringify(props) ?? "undefined"
    }`;
    const t0 = performance.now();
    const t = Date.now();

    // Tier 1 — memória.
    const cached = store.get(vkey);
    if (cached && t < cached.staleUntil) {
      const isStale = t >= cached.freshUntil;
      if (isStale) bgRefresh(vkey, props);
      touch(vkey, cached);
      served(isStale ? "STALE-HIT" : "HIT", t0, "l1");
      return cached.value;
    }

    // Dedup: chamadas concorrentes juntam-se à cadeia lookup+fetch em voo.
    const pending = inflight.get(vkey);
    if (pending) {
      recordCacheMetric(true, name, undefined, "cachedLoader", "inflight");
      return pending.then((r) => {
        recordLoaderMetric(name, performance.now() - t0, "HIT");
        return r;
      });
    }

    const promise = (async (): Promise<TResult> => {
      // Tier 2 — Cache API (persistente, por colo).
      const hit = await getPersistent<TResult>(vkey, name);
      // A janela sai do `ttlOf` do valor GRAVADO, não do profile: uma entrada
      // negativa vence em `negative.ttlMs` mesmo estando fisicamente no L2.
      const window = hit ? ttlOf(hit.value) : undefined;
      if (hit && window && hit.ageMs < window.total) {
        // Hidrata a memória preservando a janela de frescor restante.
        setEntry(vkey, hit.value, hit.ageMs, hit.bytes);
        const isStale = hit.ageMs >= window.fresh;
        if (isStale) bgRefresh(vkey, props);
        served(isStale ? "STALE-HIT" : "HIT", t0, "l2");
        return hit.value;
      }

      // Tier 3 — origem.
      recordCacheMetric(false, name, "MISS", "cachedLoader", "origin");
      try {
        const result = await origin(props);
        recordLoaderMetric(name, performance.now() - t0, "MISS");
        setEntry(vkey, result);
        return result;
      } catch (err) {
        recordLoaderMetric(name, performance.now() - t0, "MISS");
        recordLoaderError(name);
        throw err;
      }
    })().finally(() => inflight.delete(vkey));

    inflight.set(vkey, promise);
    return promise;
  };
}
